import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  ITIRAZ_DURUM_ETIKETI,
  donemListesi,
  itirazIncelemeyeAl,
  itirazKarar,
  itirazListesi,
  itirazYenidenHesapla,
  type ItirazDurumu,
  type ItirazOzeti,
  type ItirazSorgusu,
} from '../api/donem';
import { GerekceDiyalogu } from '../bilesenler/Diyalog';
import { ItirazDurumRozeti } from '../bilesenler/DonemParcalari';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { Dugme, HataKutusu } from '../bilesenler/temel';
import { tarihMetni, zamanMetni } from '../bicim/tarih';
import { yetkiKullan } from '../yetki/yetki';

/**
 * PUANTAJ İTİRAZLARI.
 *
 * GÖREVLER AYRILIĞI: kendi itirazını karara bağlayamazsın —
 * `kararVerenAtamaId` itiraz edenle aynı olamaz (sunucu denetler).
 *
 * KARAR GEREKÇESİ ZORUNLU. Gerekçesiz red personelin ikinci kez itiraz
 * etmesine yol açar; gerekçesiz kabul denetimde "bu düzeltme neye dayanıyor"
 * sorusunu cevapsız bırakır.
 *
 * YENİDEN HESAPLAMA AYRI ADIMDIR: düzeltme devam hareketi üzerinden
 * yapıldıktan SONRA çağrılır. Karar anında çağrılsaydı henüz düzeltilmemiş
 * veriyi hesaplar ve hiçbir şey değişmezdi — bu yüzden ayrı düğme.
 */

const DURUMLAR: ItirazDurumu[] = ['acik', 'inceleniyor', 'kabul', 'red', 'iptal'];

type Eylem =
  | { tur: 'karar'; kabul: boolean; itiraz: ItirazOzeti }
  | { tur: 'yeniden-hesapla'; itiraz: ItirazOzeti };

export function DonemItiraz() {
  const sorguIstemcisi = useQueryClient();

  const kararVerebilir = yetkiKullan('ITIRAZ_KARAR');
  // `POST /itiraz/:id/yeniden-hesapla` `PUANTAJ_DUZELT`i de kabul ediyor.
  const hesaplayabilir = yetkiKullan('ITIRAZ_KARAR', 'PUANTAJ_DUZELT');

  /*
   * DÖNEM SÜZGECİ AYRI YETKİ İSTİYOR. `GET /donem` `ITIRAZ_KARAR`ı KABUL
   * ETMİYOR (`PERSONEL_GORUNTULE | DONEM_KAPAT | RAPOR_GORUNTULE`); koşulsuz
   * çağrılsaydı yalnız `ITIRAZ_KARAR` olan kullanıcı bu sekmeyi açar açmaz
   * 403 alırdı. Süzgeç ona hiç gösterilmez — itiraz listesi zaten dönemsiz
   * çalışıyor.
   */
  const donemSecebilir = yetkiKullan('PERSONEL_GORUNTULE', 'DONEM_KAPAT', 'RAPOR_GORUNTULE');

  const [suzgec, suzgecYaz] = useState<ItirazSorgusu>({});
  const [eylem, eylemYaz] = useState<Eylem | null>(null);
  const [hesapSonucu, hesapSonucuYaz] = useState<string | null>(null);

  const donemler = useQuery({
    queryKey: ['donem', 'liste'],
    queryFn: () => donemListesi(),
    enabled: donemSecebilir,
  });

  const itirazlar = useQuery({
    queryKey: ['donem', 'itiraz', suzgec],
    queryFn: () => itirazListesi(suzgec),
  });

  const tazele = async () => {
    await sorguIstemcisi.invalidateQueries({ queryKey: ['donem', 'itiraz'] });
    eylemYaz(null);
  };

  const incele = useMutation({
    mutationFn: (id: string) => itirazIncelemeyeAl(id),
    onSuccess: tazele,
  });

  const karar = useMutation({
    mutationFn: (girdi: { id: string; kabul: boolean; gerekce: string }) =>
      itirazKarar(girdi.id, girdi.kabul, girdi.gerekce),
    onSuccess: tazele,
  });

  const hesapla = useMutation({
    mutationFn: (id: string) => itirazYenidenHesapla(id),
    onSuccess: async (sonuc) => {
      hesapSonucuYaz(`Gün yeniden hesaplandı: net ${sonuc.netDakika} dakika.`);
      await tazele();
    },
  });

  const sutunlar: Sutun<ItirazOzeti>[] = [
    {
      baslik: 'Personel',
      hucre: (i) => (
        <span>
          <span className="block font-medium text-slate-900">{i.adSoyad}</span>
          <span className="block text-xs text-slate-500">
            {i.personelNo} · {i.birimAd}
          </span>
        </span>
      ),
    },
    {
      baslik: 'Gün',
      hucre: (i) => (
        <span className="whitespace-nowrap text-xs">
          {/* İtiraza konu GÜN — salt tarih, kesilerek gösterilir. */}
          <span className="block font-medium text-slate-900">{tarihMetni(i.puantajTarihi)}</span>
          <span className="block text-slate-500">{zamanMetni(i.itirazZamani)}</span>
        </span>
      ),
      daralt: true,
    },
    {
      baslik: 'Gerekçe',
      hucre: (i) => (
        <span className="block max-w-md">
          <span className="block text-sm text-slate-800">{i.gerekce}</span>
          {(i.talepEdilenGiris !== null || i.talepEdilenCikis !== null) && (
            <span className="mt-0.5 block text-xs text-slate-500">
              Talep: {zamanMetni(i.talepEdilenGiris)} – {zamanMetni(i.talepEdilenCikis)}
            </span>
          )}
        </span>
      ),
    },
    { baslik: 'Durum', hucre: (i) => <ItirazDurumRozeti durum={i.durum} />, daralt: true },
    {
      baslik: '',
      hucre: (i) => (
        <span className="flex justify-end gap-1 whitespace-nowrap">
          {kararVerebilir && i.durum === 'acik' && (
            <Eylemci etiket="İncelemeye al" tikla={() => incele.mutate(i.itirazId)} />
          )}
          {kararVerebilir && (i.durum === 'acik' || i.durum === 'inceleniyor') && (
            <>
              <Eylemci
                etiket="Kabul"
                tikla={() => eylemYaz({ tur: 'karar', kabul: true, itiraz: i })}
              />
              <Eylemci
                etiket="Red"
                tehlike
                tikla={() => eylemYaz({ tur: 'karar', kabul: false, itiraz: i })}
              />
            </>
          )}
          {/*
            YENİDEN HESAPLA YALNIZ KABUL EDİLMİŞ İTİRAZDA. Düzeltme devam
            hareketi üzerinden yapıldıktan SONRA çağrılır; karar anında
            çağrılsaydı henüz düzeltilmemiş veriyi hesaplardı.
          */}
          {hesaplayabilir && i.durum === 'kabul' && (
            <Eylemci etiket="Yeniden hesapla" tikla={() => hesapla.mutate(i.itirazId)} />
          )}
        </span>
      ),
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {donemSecebilir && (
          <label className="text-sm text-slate-700">
            <span className="mb-1 block font-medium">Dönem</span>
            <select
              value={suzgec.donemId ?? ''}
              onChange={(olay) =>
                suzgecYaz((o) => ({
                  ...o,
                  donemId: olay.target.value === '' ? undefined : Number(olay.target.value),
                }))
              }
              className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              <option value="">Tümü</option>
              {(donemler.data ?? []).map((d) => (
                <option key={d.donemId} value={d.donemId}>
                  {tarihMetni(d.donemAy)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="text-sm text-slate-700">
          <span className="mb-1 block font-medium">Durum</span>
          <select
            value={suzgec.durum ?? ''}
            onChange={(olay) =>
              suzgecYaz((o) => ({
                ...o,
                durum: olay.target.value === '' ? undefined : (olay.target.value as ItirazDurumu),
              }))
            }
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
          >
            <option value="">Tümü</option>
            {DURUMLAR.map((d) => (
              <option key={d} value={d}>
                {ITIRAZ_DURUM_ETIKETI[d]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <HataKutusu hata={itirazlar.error} />
      <HataKutusu hata={incele.error} />
      <HataKutusu hata={hesapla.error} />

      {hesapSonucu !== null && (
        <p className="flex items-center justify-between gap-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          {hesapSonucu}
          <Dugme tur="ikincil" tikla={() => hesapSonucuYaz(null)} cocuk="Tamam" />
        </p>
      )}

      <Tablo
        sutunlar={sutunlar}
        satirlar={itirazlar.data ?? []}
        anahtar={(i) => i.itirazId}
        bekliyor={itirazlar.isPending}
        bosMetin="Bu süzgeçle itiraz bulunamadı."
      />

      {eylem !== null && eylem.tur === 'karar' && (
        <GerekceDiyalogu
          baslik={eylem.kabul ? 'İtirazı kabul et' : 'İtirazı reddet'}
          aciklama={`${eylem.itiraz.adSoyad} · ${tarihMetni(eylem.itiraz.puantajTarihi)} · ${eylem.itiraz.gerekce}`}
          etiket="Karar gerekçesi"
          ipucu={
            eylem.kabul
              ? 'Zorunlu — denetimde “bu düzeltme neye dayanıyor” sorusunun cevabıdır.'
              : 'Zorunlu — gerekçesiz red ikinci bir itiraza yol açar.'
          }
          onayEtiketi={eylem.kabul ? 'Kabul et' : 'Reddet'}
          onayTuru={eylem.kabul ? 'birincil' : 'tehlike'}
          // `ItirazKararDto.kararGerekcesi` @MaxLength(1000).
          enCokKarakter={1000}
          bekliyor={karar.isPending}
          hata={karar.error}
          kapat={() => eylemYaz(null)}
          onayla={(gerekce) =>
            karar.mutate({ id: eylem.itiraz.itirazId, kabul: eylem.kabul, gerekce })
          }
        />
      )}
    </div>
  );
}

function Eylemci({
  etiket,
  tikla,
  tehlike = false,
}: {
  etiket: string;
  tikla: () => void;
  tehlike?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={tikla}
      className={`rounded px-2 py-1 text-xs ${
        tehlike ? 'text-red-700 hover:bg-red-50' : 'text-kurum-700 hover:bg-kurum-50'
      }`}
    >
      {etiket}
    </button>
  );
}
